import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import {
  MinimalAlert,
  MinimalInfoPanel,
  MinimalBadge,
  MinimalButton,
  MinimalCard,
  MinimalEmptyState,
  MinimalSegmentedControl,
  MinimalOption,
  type MinimalTone,
} from '@ovasabi/ui-minimal'
import { Money, PageHeader, Screen, SectionHeading } from '../../components/chow'
import { PlateIcon } from '../../components/icons'
import {
  useChefMenuSnapshot,
  useDishSnapshot,
} from '../../generated/prototypeRuntime'
import { allowSeedContent, commandPayload, commands, isLiveRuntime, mediaUrl } from '../../lib/runtime'
import { ChefStyle, confirmedUpsert, echoUpsert, enumLabel, enumShort, getStore, ImageThumb, type StoreRecord } from './chefShared'
import { useChooseChow, usePageContext } from './ChooseChowContext'
import { DishEditModal } from './DishEditModal'
import { uniqueDishLibrary } from './dishLibrary'
import { AddKitchenLocationModal, useKitchenLocations } from './kitchenCatalog'

type View = 'menus' | 'library'

/* Dish status as the chef reads it: an `available` dish is live and orderable
   right now (green), not a neutral state — this reinforces the "available dishes
   are already on your menu" explainer. `hidden` is an action item (warning). */
const DISH_STATUS_META: Record<string, { label: string; tone: MinimalTone }> = {
  available: { label: 'Live', tone: 'success' },
  sold_out: { label: 'Sold out', tone: 'neutral' },
  hidden: { label: 'Hidden', tone: 'warning' },
}
const dishStatusMeta = (status: string): { label: string; tone: MinimalTone } =>
  DISH_STATUS_META[status] ?? { label: status.replace(/_/g, ' '), tone: 'neutral' }

/* Menu lifecycle as the chef reads it: published is live (green), draft is
   private (neutral), paused is off but recoverable (warning). */
const MENU_STATUS_META: Record<string, { label: string; tone: MinimalTone }> = {
  published: { label: 'Published', tone: 'success' },
  draft: { label: 'Draft', tone: 'neutral' },
  paused: { label: 'Paused', tone: 'warning' },
}
const menuStatusMeta = (status: string): { label: string; tone: MinimalTone } =>
  MENU_STATUS_META[status] ?? { label: status.replace(/_/g, ' '), tone: 'neutral' }

const VIEWS: readonly MinimalOption<View>[] = [
  { value: 'menus', label: 'Menus' },
  { value: 'library', label: 'Dish library' },
]

const HeroCTA = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${minimalVars.space.xs};
  width: 100%;
  padding: ${minimalVars.space.sm};
  border-radius: ${minimalVars.radius.md};
  border: 0;
  background: ${minimalVars.color.brand};
  color: white;
  cursor: pointer;
  text-align: left;
  font: inherit;
`

const HeroTitle = styled.div`
  font-weight: ${minimalVars.typography.weightSemibold};
  font-size: ${minimalVars.typography.bodySize};
`

const HeroSub = styled.div`
  font-size: ${minimalVars.typography.captionSize};
  opacity: 0.85;
`

const HeroArrow = styled.span`
  /* A decorative arrow mark, not text. h2Size is 22px but carries a heading
     role, so the mark keeps a fixed size. */
  font-size: 22px;
  line-height: 1;
`

/* ── menu card ────────────────────────────────────────────────────────
   Matches the dish-card standard used on the customer side: a wide cover
   image carrying the status chip, a compact copy block, then a quiet action
   row. Actions are ghost-weight text so the card reads as content, not as a
   toolbar — the cover is the loudest thing on it. */

/* Menus read like the customer dish cards now: a capped cover, then a compact
   copy block and a quiet action row — laid out on a responsive grid so a chef
   with several menus sees a gallery, not a stack of giant blank covers. */
const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${minimalVars.space.sm};

  /* 600px matches no named stop; it sits under the page stop. */
  @media (min-width: 600px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
`

const MenuCard = styled(MinimalCard)`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  /* The cover bleeds to the card's edges; the copy and action rows carry their
     own padding. MinimalCard has no "none" size, so the override lives here. */
  padding: 0;
`

const MenuCover = styled.div<{ $src?: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  /* Empty covers sit on the same branded gradient the customer cards use, so a
     menu without a photo still looks designed rather than blank. */
  background-color: ${minimalVars.color.bgSurfaceAlt};
  background-image: ${({ $src }) => {
    // Full card width on a phone is ~340 CSS px, ~700 device px.
    const resolved = mediaUrl($src, 768)
    return resolved
      ? `url(${resolved})`
      : `linear-gradient(140deg, ${minimalVars.color.brandSoft}, ${minimalVars.color.bgSurfaceAlt})`
  }};
  background-size: cover;
  background-position: center;
  display: grid;
  place-items: center;
  color: ${minimalVars.color.brand};
  opacity: ${({ $src }) => ($src ? 1 : 0.9)};
`

const MenuCoverBadge = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1;
`

const MenuBody = styled.div`
  flex: 1;
  display: grid;
  gap: 3px;
  align-content: start;
  padding: ${minimalVars.space.xs} ${minimalVars.space.sm} 0;
`

const MenuTitle = styled.h3`
  margin: 0;
  font-family: ${minimalVars.typography.displayFamily};
  font-weight: ${minimalVars.typography.weightBold};
  /* A card name is a heading at body size. bodySize would relabel it as body
     text, so the 16px stays fixed. */
  font-size: 1rem;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: ${minimalVars.color.textPrimary};
`

const MenuActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${minimalVars.space['2xs']};
  padding: ${minimalVars.space.xs} ${minimalVars.space.sm};

  /* Delete is destructive: it sits apart, at the far end of the row. */
  & > [data-action='delete'] {
    margin-left: auto;
  }
`

const advance = async (menu: StoreRecord, status: 'published' | 'paused' | 'archived') => {
  try {
    const response = await commands.menu.publishMenu(String(menu.id), status)
    confirmedUpsert('menu.chef_menus', { ...menu, ...commandPayload(response), status })
  } catch (error) {
    if (isLiveRuntime() || !allowSeedContent()) return error instanceof Error ? error.message : 'Could not change menu status.'
    echoUpsert('menu.chef_menus', { ...menu, status })
  }
  return ''
}

/**
 * Flip a dish's status. Returns an error string in live mode when the backend
 * refuses — e.g. going `available` on a dish missing its region/allergens fails
 * the publish gate (`dish_region_required` / `dish_allergens_required`). We must
 * not swallow that and echo a fake `available`, or the dish reads as published
 * while the server keeps it hidden and it reverts on the next refresh.
 */
const setDishStatus = async (
  dish: StoreRecord,
  status: 'available' | 'sold_out' | 'hidden',
): Promise<string> => {
  try {
    await commands.menu.setDishStatus(String(dish.id), status)
  } catch (error) {
    if (isLiveRuntime()) {
      const detail = error instanceof Error ? error.message.trim() : ''
      return detail
        ? `Could not update this dish: ${detail}`
        : 'Could not update this dish. Please try again.'
    }
    /* offline: fall through to the local echo */
  }
  confirmedUpsert('menu.dishes', { ...dish, status })
  return ''
}

/** Remove a record from a local projection store (offline echo of a delete). */
const echoRemove = (storeKey: string, id: string) => {
  getStore(storeKey)?.remove?.(id)
}

/** Whatever the transport said, reduced to one line a chef can act on. */
const commandMessage = (error: unknown, subject: string): string => {
  const detail = error instanceof Error ? error.message : String(error ?? '')
  return detail
    ? `Could not delete this ${subject}: ${detail}`
    : `Could not delete this ${subject}. Check your connection and try again.`
}

/**
 * Permanently delete a dish. Deletes are irreversible and cascade (reviews,
 * meal-plan entries), so we confirm first. The optimistic echo drops the row
 * locally; live mode converges via the tombstone projection.
 */
const deleteDish = async (dish: StoreRecord): Promise<string> => {
  const id = String(dish.id ?? '')
  if (!id) return ''
  if (!window.confirm(`Delete “${String(dish.name ?? 'this dish')}”? This can't be undone.`)) return ''
  try {
    await commands.menu.deleteDish(id)
  } catch (error) {
    if (isLiveRuntime()) return commandMessage(error, 'dish')
  }
  echoRemove('menu.dishes', id)
  return ''
}

/**
 * Permanently delete a menu. Its attached dishes go with it, so the confirm
 * spells that out.
 *
 * A live delete that fails must *not* drop the row locally: the projection
 * would put it straight back on the next sweep, which reads as "delete does
 * nothing". So we only echo the removal when the command succeeded, or when
 * there is no backend to succeed (dummy/offline mode), and report the failure
 * back to the caller.
 */
const deleteMenu = async (menu: StoreRecord, dishCount: number): Promise<string> => {
  const id = String(menu.id ?? '')
  if (!id) return ''
  const tail = dishCount > 0 ? ` and its ${dishCount} dish${dishCount === 1 ? '' : 'es'}` : ''
  if (!window.confirm(`Delete “${String(menu.title ?? 'this menu')}”${tail}? This can't be undone.`))
    return ''
  try {
    await commands.menu.deleteMenu(id)
  } catch (error) {
    if (isLiveRuntime()) return commandMessage(error, 'menu')
  }
  echoRemove('menu.chef_menus', id)
  return ''
}

const MenusList = ({ menus, dishesByMenu }: {
  menus: readonly StoreRecord[]
  dishesByMenu: Record<string, StoreRecord[]>
}) => {
  const navigate = useNavigate()
  const [error, setError] = useState('')
  if (menus.length === 0) {
    return (
      <MinimalEmptyState
        icon={<PlateIcon />}
        title="No menus yet"
        description="Build your first menu — drafts stay private until you publish."
      />
    )
  }
  return (
    <>
      {error ? (
        <MinimalAlert tone="danger" title="Menu change failed">
          {error}
        </MinimalAlert>
      ) : null}
      <MenuGrid>
      {menus.map((menu) => {
        const id = String(menu.id ?? '')
        const status = enumShort(menu.status, 'draft')
        const cover = menu.cover_image_url ? String(menu.cover_image_url) : undefined
        const dishes = dishesByMenu[id] ?? []
        const published = status === 'published'
        return (
          <MenuCard key={id} padding="sm">
            <MenuCover $src={cover}>
              <MenuCoverBadge>
                <MinimalBadge tone={menuStatusMeta(status).tone} emphasis="solid" size="sm">
                  {menuStatusMeta(status).label}
                </MinimalBadge>
              </MenuCoverBadge>
              {!cover && <PlateIcon size={32} />}
            </MenuCover>
            <MenuBody>
              <MenuTitle>{String(menu.title ?? 'Untitled menu')}</MenuTitle>
              <ChefStyle.Sub>
                {enumLabel(menu.cadence, 'weekly')} · {dishes.length} dish
                {dishes.length === 1 ? '' : 'es'}
                {menu.week_start ? ` · wk ${String(menu.week_start)}` : ''}
              </ChefStyle.Sub>
            </MenuBody>
            <MenuActions>
              <MinimalButton size="sm" variant="ghost" onClick={() => navigate(`/menu/${id}/edit`)}>
                Edit
              </MinimalButton>
              <MinimalButton
                size="sm"
                variant="ghost"
                onClick={() => {
                  setError('')
                  void advance(menu, published ? 'paused' : 'published').then(setError)
                }}
              >
                {published ? 'Pause' : 'Publish'}
              </MinimalButton>
              <MinimalButton
                size="sm"
                variant="ghost"
                data-action="delete"
                style={{ color: 'var(--minimal-color-danger)' }}
                onClick={() => {
                  setError('')
                  void deleteMenu(menu, dishes.length).then(setError)
                }}
              >
                Delete
              </MinimalButton>
            </MenuActions>
          </MenuCard>
        )
      })}
      </MenuGrid>
    </>
  )
}

/* Library card footer: status badge on the left, the edit/stock/delete actions
   grouped together on the right — not sprayed across the whole card width. */
const LibraryFooter = styled.div`
  display: grid;
  gap: ${minimalVars.space['2xs']};
  margin-top: auto;
  padding-top: ${minimalVars.space.xs};
`

const LibraryActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${minimalVars.space['2xs']};
  > button { min-height: 40px; white-space: normal; }
`
const DishCard = styled(MinimalCard)`
  display: flex;
  flex-direction: column;
  min-height: 224px;
  > div { display: flex; flex: 1; flex-direction: column; }
`

const DishLibrary = ({
  dishes,
  chefProfileId,
  onAddDish,
}: {
  dishes: readonly StoreRecord[]
  chefProfileId: string
  onAddDish?: () => void
}) => {
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const [editing, setEditing] = useState<StoreRecord | null>(null)
  if (dishes.length === 0) {
    return (
      <MinimalEmptyState
        icon={<PlateIcon />}
        title="No dishes in your library"
        description="Add your first dish to start building your kitchen's menu."
        action={
          onAddDish ? (
            <MinimalButton variant="primary" size="sm" onClick={onAddDish}>
              Add your first dish
            </MinimalButton>
          ) : undefined
        }
      />
    )
  }
  return (
    <>
      {error ? (
        <MinimalAlert tone="danger" title="Couldn’t update dish">
          {error}
        </MinimalAlert>
      ) : null}
      <MenuGrid>
      {dishes.map((dish) => {
        const id = String(dish.id ?? '')
        const status = enumShort(dish.status, 'available')
        const img = dish.image_url ? String(dish.image_url) : undefined
        return (
          <DishCard key={id} padding="sm">
            <ChefStyle.ThumbRow>
              <ImageThumb $src={img} $size={64} />
              <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 2 }}>
                <MenuTitle style={{ fontSize: '0.9375rem' }}>{String(dish.name ?? '')}</MenuTitle>
                <ChefStyle.Sub style={{ textTransform: 'capitalize' }}>
                  {enumLabel(dish.category, 'main')}
                </ChefStyle.Sub>
                <Money amount={Number(dish.price_minor ?? 0) / 100} tone="brand" size="sm" />
              </div>
            </ChefStyle.ThumbRow>
            {status === 'hidden' && (
              <ChefStyle.Sub style={{ marginTop: 6, color: 'var(--minimal-color-warning, #e0a008)' }}>
                Hidden from customers — add a region to publish.
              </ChefStyle.Sub>
            )}
            <LibraryFooter>
              <MinimalBadge tone={dishStatusMeta(status).tone} emphasis="soft" size="sm">
                {dishStatusMeta(status).label}
              </MinimalBadge>
              <LibraryActions>
                <MinimalButton size="sm" variant="ghost" onClick={() => navigate(`/promotions?dish=${encodeURIComponent(id)}`)}>Offer discount</MinimalButton>
                <MinimalButton size="sm" variant="ghost" onClick={() => setEditing(dish)}>
                  Edit
                </MinimalButton>
                {status === 'hidden' ? (
                  <MinimalButton size="sm" variant="primary" onClick={() => {
                    setError('')
                    void setDishStatus(dish, 'available').then(setError)
                  }}>
                    Publish dish
                  </MinimalButton>
                ) : (
                  <MinimalButton
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setError('')
                      void setDishStatus(
                        dish,
                        status === 'available' ? 'sold_out' : 'available',
                      ).then(setError)
                    }}
                  >
                    {status === 'available' ? 'Mark sold out' : 'Restock'}
                  </MinimalButton>
                )}
                <MinimalButton size="sm" variant="ghost" style={{ color: 'var(--minimal-color-danger)' }} onClick={() => {
                    setError('')
                    void deleteDish(dish).then(setError)
                  }}>
                  Delete
                </MinimalButton>
              </LibraryActions>
            </LibraryFooter>
          </DishCard>
        )
      })}
      </MenuGrid>
      <DishEditModal
        key={String(editing?.id ?? 'none')}
        dish={editing}
        chefProfileId={chefProfileId}
        onClose={() => setEditing(null)}
      />
    </>
  )
}

/**
 * The Menus hub. Two tabs: the chef's menus (manageable list) and the dish
 * library (every dish they've created, reusable on any menu). The primary
 * CTA opens the dedicated builder screen at /menu/new so the chef can focus
 * on creation without losing context of the lists.
 */
export const ChefMenuHub = () => {
  const navigate = useNavigate()
  const { profileId } = useChooseChow()
  const page = usePageContext('/menu')
  const menusSnap = useChefMenuSnapshot() as { records: readonly StoreRecord[] }
  const dishesSnap = useDishSnapshot() as { records: readonly StoreRecord[] }
  const kitchen = useKitchenLocations(profileId)
  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useState<View>(searchParams.get('view') === 'library' ? 'library' : 'menus')
  const [isCreatingDish, setIsCreatingDish] = useState(false)
  const [showAddLocation, setShowAddLocation] = useState(false)

  const changeView = (next: View) => {
    setView(next)
    // Keep the URL honest so a back/forward or refresh lands on the same tab.
    const params = new URLSearchParams(searchParams)
    if (next === 'library') params.set('view', 'library')
    else params.delete('view')
    setSearchParams(params, { replace: true })
  }

  const menus = useMemo(
    () =>
      menusSnap.records.filter((m) => {
        const owner = String(m.chef_profile_id ?? '')
        return Boolean(profileId) && owner === profileId
      }),
    [menusSnap.records, profileId],
  )

  const library = useMemo(
    () =>
      uniqueDishLibrary(dishesSnap.records.filter((d) => {
        const owner = String(d.chef_profile_id ?? '')
        return Boolean(profileId) && owner === profileId
      })),
    [dishesSnap.records, profileId],
  )

  const dishesByMenu = useMemo(() => {
    const out: Record<string, StoreRecord[]> = {}
    for (const d of dishesSnap.records) {
      if (!profileId || String(d.chef_profile_id ?? '') !== profileId) continue
      const key = String(d.menu_id ?? '')
      if (!key) continue
      out[key] = out[key] ?? []
      out[key].push(d)
    }
    return out
  }, [dishesSnap.records, profileId])

  return (
    <Screen header={<PageHeader title={page.title} accentDot />}>
      <HeroCTA
        type="button"
        whileTap={{ scale: 0.98 }}
        whileHover={{ y: -1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 24 }}
        onClick={() => {
          if (view === 'library') {
            setIsCreatingDish(true)
          } else {
            navigate('/menu/new')
          }
        }}
      >
        <div>
          <HeroTitle>{view === 'library' ? 'Add new dish' : 'Build a new menu'}</HeroTitle>
          <HeroSub>
            {view === 'library'
              ? 'Create a dish in your library to use on any menu.'
              : 'Pick from your library or add fresh dishes as you go.'}
          </HeroSub>
        </div>
        <HeroArrow aria-hidden>→</HeroArrow>
      </HeroCTA>

      {!kitchen.loading && kitchen.locations.length === 0 && (
        <MinimalAlert
          tone="warning"
          title="Kitchen address required"
          action={
            <MinimalButton size="sm" variant="primary" onClick={() => setShowAddLocation(true)}>
              Add kitchen address
            </MinimalButton>
          }
        >
          Add your kitchen street address and area before publishing dishes or menus.
        </MinimalAlert>
      )}

      <MinimalInfoPanel
        title="Dishes first. Menus bring them together."
        description="Three steps: add where you cook, add your dishes, then publish a menu when you’re ready for orders."
        icon={<PlateIcon />}
      />

      <MinimalSegmentedControl
        ariaLabel="Menus or library"
        options={VIEWS}
        value={view}
        onChange={changeView}
      />

      <motion.div
        key={view}
        style={{ display: 'grid', gap: 10 }}
        /* Transform-only entrance: page content must never be faded in, because
           a mid-tween opacity both hides real text from a reader and drops it
           below the contrast floor while it animates. */
        initial={{ y: 6 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
      >
        <SectionHeading
          title={
            view === 'menus' ? `Your menus · ${menus.length}` : `Your dishes · ${library.length}`
          }
        />
        {view === 'menus' ? (
          <MenusList menus={menus} dishesByMenu={dishesByMenu} />
        ) : (
          <DishLibrary
            dishes={library}
            chefProfileId={profileId}
            onAddDish={() => setIsCreatingDish(true)}
          />
        )}
      </motion.div>

      {isCreatingDish && (
        <DishEditModal
          isOpen
          dish={null}
          chefProfileId={profileId}
          onClose={() => setIsCreatingDish(false)}
        />
      )}

      <AddKitchenLocationModal
        profileId={profileId}
        open={showAddLocation}
        onClose={() => setShowAddLocation(false)}
        onSaved={() => kitchen.reload?.()}
      />
    </Screen>
  )
}
